import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type PersonalOffice,
  useMyPersonalOffice,
  useUpdatePersonalOffice,
} from "@/hooks/use-personal-offices";
import {
  useUpdateVyllageProfile,
  useVyllageAward,
  useVyllageCheckIn,
  useVyllageProfile,
} from "@/hooks/use-vyllage-profile";
import { useSession } from "@/lib/auth-client";
import { type OfficeActionId, officeActionIdSchema } from "@/lib/office/actions";
import { normalizeAvatarLook } from "@/lib/office/avatar-look";
import type { AwardKind } from "@/lib/office/awards";
import type { PersonalOfficeSnapshot } from "@/lib/office/protocol";
import { unlocksEarnedFromPoints } from "@/lib/office/unlocks";
import {
  DEFAULT_COLORS,
  type FeedItem,
  loadPersisted,
  MIGRATED_KEY,
  type SideTab,
  SPATIAL_AV_TIP,
  STATUSES,
  type StatusId,
  savePersisted,
  type ToastItem,
  UNLOCKS,
} from "./constants";
import { useOfficeMultiplayer } from "./use-office-multiplayer";
import type { AvatarColors, VyllagePerson, VyllageWorldElement } from "./world-engine";

export type MeetingState = {
  /** Seated in a private zone — open Zoom-style meeting view. */
  active: boolean;
  /** At least one peer shares the private zone. */
  live: boolean;
  sitting: boolean;
  inside: boolean;
  presenter: string;
  topic: string;
  roomId: string | null;
  personal: boolean;
  participants: Array<{
    id: string;
    name: string;
    camOn: boolean;
    micOn: boolean;
    color: string;
    sharing?: boolean;
  }>;
};

export function useVyllageState() {
  const worldRef = useRef<VyllageWorldElement | null>(null);
  const chatInputRef = useRef<HTMLInputElement | null>(null);
  const feedElRef = useRef<HTMLDivElement | null>(null);
  const toastIdRef = useRef(0);
  const visitedRef = useRef<Record<string, boolean>>({});
  const meetingAwardedRef = useRef(false);
  const wasMeetingSittingRef = useRef(false);
  const checkInSentRef = useRef(false);
  const lastDayRef = useRef<string | null>(null);
  const unlockedIdsRef = useRef<string[]>([]);
  const enteredRef = useRef(false);
  const nameRef = useRef("You");
  const colorsRef = useRef<AvatarColors>(DEFAULT_COLORS);
  const zoneRef = useRef("The Commons");
  const performActionRef = useRef<((id: OfficeActionId) => void) | null>(null);

  const [entered, setEntered] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [name, setName] = useState("You");
  const [colors, setColors] = useState<AvatarColors>(DEFAULT_COLORS);
  const [profileHydrated, setProfileHydrated] = useState(false);
  const autoEnteredRef = useRef(false);
  const [status, setStatus] = useState<StatusId>("available");
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [mic, setMic] = useState(true);
  const [cam, setCam] = useState(true);
  const [zone, setZone] = useState("The Commons");
  const [zoneId, setZoneId] = useState("commons");
  const [zonePriv, setZonePriv] = useState(false);
  const [zonePersonal, setZonePersonal] = useState(false);
  const [zoneDoorStatus, setZoneDoorStatus] = useState<"open" | "closed" | null>(null);
  const [laptopOpen, setLaptopOpen] = useState(false);
  const [people, setPeople] = useState<VyllagePerson[]>([]);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [points, setPoints] = useState(0);
  const [streak, setStreak] = useState(5);
  const [tab, setTab] = useState<SideTab>("chat");
  const [meeting, setMeeting] = useState<MeetingState>({
    active: false,
    live: false,
    sitting: false,
    inside: false,
    presenter: "",
    topic: "Live huddle",
    roomId: null,
    personal: false,
    participants: [],
  });
  /** User can minimize Zoom grid while still seated (map visible). */
  const [meetingViewOpen, setMeetingViewOpen] = useState(false);
  const [unlockedIds, setUnlockedIds] = useState<string[]>([]);
  const [officeSnapshots, setOfficeSnapshots] = useState<PersonalOfficeSnapshot[]>([]);
  const [showCustomizeOffice, setShowCustomizeOffice] = useState(false);

  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;
  const { data: myOfficeFromApi, refetch: refetchMyOffice } = useMyPersonalOffice();
  const updateOffice = useUpdatePersonalOffice();
  const { data: vyllageProfile, isLoading: profileLoading } = useVyllageProfile();
  const updateProfile = useUpdateVyllageProfile();
  const checkInMutation = useVyllageCheckIn();
  const awardMutation = useVyllageAward();
  const checkInMutateRef = useRef(checkInMutation.mutate);
  const awardMutateRef = useRef(awardMutation.mutate);
  checkInMutateRef.current = checkInMutation.mutate;
  awardMutateRef.current = awardMutation.mutate;

  const myOffice: PersonalOffice | null = useMemo(() => {
    if (myOfficeFromApi) return myOfficeFromApi;
    if (!userId) return null;
    const snap = officeSnapshots.find((o) => o.ownerUserId === userId);
    if (!snap) return null;
    return {
      id: snap.id,
      slotKey: snap.slotKey,
      displayName: snap.displayName,
      ownerUserId: snap.ownerUserId,
      ownerName: null,
      ownerEmail: null,
      doorStatus: snap.doorStatus,
      decorIds: snap.decorIds,
      wallArtFileId: snap.wallArtFileId ?? null,
      wallArtPlaceholder: snap.wallArtPlaceholder ?? null,
      theme: {
        wallColor: snap.theme?.wallColor ?? "#E6EBF2",
        rugColor: snap.theme?.rugColor ?? "#C8D0DC",
      },
      createdAt: "",
      updatedAt: "",
    };
  }, [myOfficeFromApi, officeSnapshots, userId]);

  /** True when the local player is inside their own personal pod (zone), seated or not. */
  const inOwnOffice = Boolean(myOffice && zonePersonal && zoneId === myOffice.slotKey);

  // Close laptop if we leave our pod (including walking into someone else's office).
  useEffect(() => {
    if (!inOwnOffice) setLaptopOpen(false);
  }, [inOwnOffice]);

  useEffect(() => {
    enteredRef.current = entered;
  }, [entered]);
  useEffect(() => {
    nameRef.current = name;
  }, [name]);
  useEffect(() => {
    colorsRef.current = colors;
  }, [colors]);
  useEffect(() => {
    zoneRef.current = zone;
  }, [zone]);

  // Hydrate from server profile (with optional one-time localStorage look merge)
  useEffect(() => {
    if (!vyllageProfile || profileHydrated) return;

    let nameNext = vyllageProfile.displayName;
    let colorsNext = vyllageProfile.avatarColors;
    // Progression is server-owned; awards are paused until secure endpoints (PR3).
    const pointsNext = vyllageProfile.points;
    const streakNext = vyllageProfile.streak;
    const unlockedNext = unlocksEarnedFromPoints(vyllageProfile.points, vyllageProfile.unlockedIds);
    const lastDay = vyllageProfile.lastCheckInDay;
    const lookConfirmed = Boolean(vyllageProfile.lookSavedAt);

    try {
      if (localStorage.getItem(MIGRATED_KEY) !== "1") {
        const local = loadPersisted();
        const serverFresh =
          !lookConfirmed &&
          vyllageProfile.avatarColors.style === DEFAULT_COLORS.style &&
          vyllageProfile.avatarColors.shirt === DEFAULT_COLORS.shirt;
        // Migrate look only — never client-write points / streak / unlocks.
        if (local && serverFresh && local.name !== "You") {
          nameNext = local.name.slice(0, 64);
          colorsNext = normalizeAvatarLook(local.colors);
          updateProfile.mutate({
            displayName: nameNext,
            avatarColors: colorsNext,
          });
        }
        localStorage.setItem(MIGRATED_KEY, "1");
      }
    } catch {
      // ignore storage errors
    }

    lastDayRef.current = lastDay;
    unlockedIdsRef.current = unlockedNext;
    nameRef.current = nameNext;
    colorsRef.current = colorsNext;
    setName(nameNext);
    setColors(colorsNext);
    setPoints(pointsNext);
    setStreak(streakNext);
    setUnlockedIds(unlockedNext);
    setProfileHydrated(true);

    // Full avatar onboarding only when look was never confirmed in the ready room.
    // Saved looks always skip the heavy modal (Step into HQ / returning users).
    // Also skip when ?ready=1 — user already finished the ready room this session.
    const readyParam =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("ready") === "1";
    setShowOnboarding(!lookConfirmed && !readyParam);
  }, [vyllageProfile, profileHydrated, updateProfile]);

  // Soft local paint while the profile query is still spinning — never mark hydrated
  // here so the real profile can still drive lookSavedAt / onboarding gates.
  useEffect(() => {
    if (profileLoading || profileHydrated || vyllageProfile) return;
    const saved = loadPersisted();
    if (!saved) return;
    nameRef.current = saved.name;
    colorsRef.current = saved.colors;
    setName(saved.name);
    setColors(saved.colors);
  }, [profileLoading, profileHydrated, vyllageProfile]);

  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Debounced look sync — never writes progression fields. */
  const persist = useCallback(
    (patch?: { name?: string; colors?: AvatarColors }) => {
      const next = {
        name: patch?.name ?? name,
        colors: patch?.colors ?? colors,
        points,
        streak,
        lastDay: lastDayRef.current,
        unlockedIds: unlockedIdsRef.current,
      };
      savePersisted(next);
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        persistTimerRef.current = null;
        updateProfile.mutate({
          displayName: next.name.slice(0, 64),
          avatarColors: next.colors,
        });
      }, 700);
    },
    [name, colors, points, streak, updateProfile],
  );

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, []);

  const toast = useCallback((text: string) => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, text }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3400);
  }, []);

  /** Apply a server award response into local progression UI. */
  const applyAwardResult = useCallback(
    (result: {
      data: {
        points: number;
        streak: number;
        lastCheckInDay: string | null;
        unlockedIds: string[];
      };
      awarded: boolean;
      delta: number;
      reason: string;
    }) => {
      const unlockedNext = unlocksEarnedFromPoints(result.data.points, result.data.unlockedIds);
      unlockedIdsRef.current = unlockedNext;
      lastDayRef.current = result.data.lastCheckInDay;
      setPoints(result.data.points);
      setStreak(result.data.streak);
      setUnlockedIds(unlockedNext);
      if (result.awarded && result.delta > 0) {
        toast(`+${result.delta} pts — ${result.reason}`);
      }
    },
    [toast],
  );

  /**
   * Server-owned awards. Kind is allowlisted; deltas/caps live on the server.
   */
  const requestAward = useCallback(
    (kind: AwardKind) => {
      awardMutateRef.current(kind, {
        onSuccess: (result) => applyAwardResult(result),
      });
    },
    [applyAwardResult],
  );

  const pushFeed = useCallback((item: FeedItem) => {
    setFeed((prev) => [...prev.slice(-49), item]);
    requestAnimationFrame(() => {
      const el = feedElRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const bindWorld = useCallback(
    (el: VyllageWorldElement) => {
      worldRef.current = el;
      el.configure({
        radius: 7,
        showNames: true,
        name: nameRef.current,
        colors: colorsRef.current,
      });

      const onZone = (e: Event) => {
        const detail = (
          e as CustomEvent<{
            id: string;
            name: string;
            priv?: boolean;
            personal?: boolean;
            doorStatus?: "open" | "closed" | null;
          }>
        ).detail;
        setZone(detail.name);
        setZoneId(detail.id);
        setZonePriv(Boolean(detail.priv));
        setZonePersonal(Boolean(detail.personal));
        setZoneDoorStatus(detail.doorStatus ?? null);
        // Leave laptop when walking out of your office
        if (!detail.personal) setLaptopOpen(false);
        if (enteredRef.current && !visitedRef.current[detail.id]) {
          visitedRef.current[detail.id] = true;
          const tour = ["focus", "meeting", "huddle_a", "huddle_b", "huddle_c", "lounge"];
          if (tour.every((id) => visitedRef.current[id])) {
            requestAward("tour");
          }
        }
        if (detail.priv && !detail.personal) {
          toast(`${detail.name} — everyone inside can hear each other`);
        }
        if (detail.personal) {
          toast(
            detail.doorStatus === "open"
              ? `${detail.name} — open to guests · private audio`
              : `${detail.name} — private office`,
          );
        }
        if (detail.id === "focus") toast("Focus Zone — sounds are muffled, camera pauses");
      };

      const onWorldToast = (e: Event) => {
        const detail = (e as CustomEvent<{ text: string }>).detail;
        if (detail?.text) toast(detail.text);
      };

      const onStatus = (e: Event) => {
        const detail = (e as CustomEvent<{ status: string; auto?: boolean }>).detail;
        const next = STATUSES.find((s) => s.id === detail.status);
        if (next) setStatus(next.id);
        if (detail.auto && next) toast(`Status set to ${next.label} automatically`);
      };

      const onNear = (e: Event) => {
        const detail = (e as CustomEvent<{ people: VyllagePerson[] }>).detail;
        setPeople(detail.people);
      };

      const onMeeting = (e: Event) => {
        const detail = (
          e as CustomEvent<{
            active: boolean;
            live?: boolean;
            sitting?: boolean;
            presenter: string;
            topic: string;
            inside: boolean;
            roomId?: string | null;
            personal?: boolean;
            participants?: Array<{
              id: string;
              name: string;
              camOn: boolean;
              micOn: boolean;
              color: string;
            }>;
          }>
        ).detail;
        const next: MeetingState = {
          active: detail.active,
          live: detail.live === true,
          sitting: detail.sitting === true,
          inside: detail.inside,
          presenter: detail.presenter,
          topic: detail.topic,
          roomId: detail.roomId ?? null,
          personal: detail.personal === true,
          participants: detail.participants ?? [],
        };
        setMeeting(next);
        // Auto-open Zoom grid only on sit transition (not peer join while minimized)
        const justSat = next.active && next.sitting && !wasMeetingSittingRef.current;
        wasMeetingSittingRef.current = next.sitting && next.active;
        if (justSat) {
          setMeetingViewOpen(true);
          setLaptopOpen(false); // meeting takes focus over laptop chrome
        } else if (!next.sitting || !next.active) {
          setMeetingViewOpen(false);
        }
        if (detail.live && detail.inside && !meetingAwardedRef.current) {
          meetingAwardedRef.current = true;
          requestAward("meeting");
        }
        if (!detail.live) meetingAwardedRef.current = false;
      };

      const onWave = () => {
        requestAward("wave");
      };

      const onActionRequest = (ev: Event) => {
        const detail = (ev as CustomEvent<{ action?: string }>).detail;
        const raw = detail?.action;
        if (!raw) return;
        const parsed = officeActionIdSchema.safeParse(raw);
        if (!parsed.success) return;
        // Defer so we call performAction after multiplayer hook is stable
        queueMicrotask(() => {
          performActionRef.current?.(parsed.data);
        });
      };

      el.addEventListener("vy-zone", onZone);
      el.addEventListener("vy-status", onStatus);
      el.addEventListener("vy-near", onNear);
      el.addEventListener("vy-meeting", onMeeting);
      el.addEventListener("vy-wave-ack", onWave);
      el.addEventListener("vy-action-request", onActionRequest);
      el.addEventListener("vy-toast", onWorldToast);

      // Hydrate office door state before multiplayer welcome (REST).
      void fetch("/api/personal-offices", { credentials: "include" })
        .then(async (res) => {
          if (!res.ok) return;
          const body: unknown = await res.json();
          if (!body || typeof body !== "object" || !("data" in body)) return;
          const data = (body as { data: unknown }).data;
          if (!Array.isArray(data)) return;
          const offices = data as PersonalOfficeSnapshot[];
          setOfficeSnapshots(offices);
          el.setPersonalOffices?.(offices);
        })
        .catch(() => {
          // offline / not authed yet — WS welcome will supply offices
        });
    },
    [requestAward, toast],
  );

  // Keep world config in sync when colors/name change after enter
  useEffect(() => {
    const el = worldRef.current;
    if (!el) return;
    el.configure({ name, colors });
  }, [name, colors]);

  const multiplayer = useOfficeMultiplayer({
    enabled: entered,
    name,
    colors,
    status,
    mic,
    cam,
    worldRef,
    onOffices: (offices) => {
      setOfficeSnapshots(offices);
      void refetchMyOffice();
    },
    onChat: (msg) => {
      pushFeed({
        id: msg.id,
        kind: "msg",
        from: msg.name,
        color: msg.color,
        text: msg.text,
        zone: msg.zone,
        vol: msg.vol,
      });
      if (msg.vol >= 0.5) {
        requestAward("chat");
      }
    },
    onPeerAction: (peerName, actionId) => {
      const first = peerName.split(" ")[0] ?? peerName;
      const past: Record<OfficeActionId, string> = {
        wave: "waved",
        jump: "jumped",
        dance: "started dancing",
        handshake: "offered a handshake",
        clap: "clapped",
        cheer: "cheered",
      };
      toast(`${first} ${past[actionId] ?? "did an action"}`);
    },
    onToast: toast,
    onMediaFlagsAlign: ({ mic: nextMic, cam: nextCam }) => {
      setMic(nextMic);
      setCam(nextCam);
    },
  });

  const statusMeta = useMemo(() => {
    return (
      STATUSES.find((s) => s.id === status) ?? {
        id: "available" as const,
        label: "Available",
        color: "#2EA86A",
      }
    );
  }, [status]);

  const setStatusManual = useCallback((id: StatusId) => {
    setStatus(id);
    setStatusMenuOpen(false);
    worldRef.current?.setStatus(id);
  }, []);

  const toggleMic = useCallback(() => {
    setMic((v) => {
      const next = !v;
      worldRef.current?.setMic(next);
      return next;
    });
  }, []);

  const toggleCam = useCallback(() => {
    setCam((v) => {
      const next = !v;
      worldRef.current?.setCam(next);
      return next;
    });
  }, []);

  const standUp = useCallback(() => {
    void multiplayer.stopScreenShare();
    worldRef.current?.standUp?.();
    setMeetingViewOpen(false);
  }, [multiplayer.stopScreenShare]);

  const minimizeMeetingView = useCallback(() => {
    setMeetingViewOpen(false);
  }, []);

  const openMeetingView = useCallback(() => {
    if (meeting.sitting && meeting.inside) setMeetingViewOpen(true);
  }, [meeting.sitting, meeting.inside]);

  const toggleScreenShare = useCallback(async () => {
    if (!meeting.sitting || !meeting.inside) return;
    if (multiplayer.sharing) {
      await multiplayer.stopScreenShare();
      return;
    }
    await multiplayer.startScreenShare();
  }, [
    meeting.sitting,
    meeting.inside,
    multiplayer.sharing,
    multiplayer.startScreenShare,
    multiplayer.stopScreenShare,
  ]);

  // Leave seat / private zone → end share so we don't keep publishing display media.
  useEffect(() => {
    if (multiplayer.sharing && (!meeting.sitting || !meeting.inside)) {
      void multiplayer.stopScreenShare();
    }
  }, [meeting.sitting, meeting.inside, multiplayer.sharing, multiplayer.stopScreenShare]);

  const performAction = useCallback(
    (actionId: OfficeActionId) => {
      const world = worldRef.current;
      if (!world?.playAction) {
        multiplayer.sendAction(actionId);
        return;
      }
      const ok = world.playAction(actionId);
      if (ok) multiplayer.sendAction(actionId);
    },
    [multiplayer],
  );

  // Keep ref in sync for keyboard path from world engine
  performActionRef.current = performAction;

  /** Default dock/E key action */
  const waveNow = useCallback(() => {
    performAction("wave");
  }, [performAction]);

  const walkTo = useCallback(
    (id: string, label: string) => {
      worldRef.current?.walkToPerson(id);
      toast(`Walking over to ${label}`);
    },
    [toast],
  );

  const walkToOffice = useCallback(
    (slotKey: string, label: string) => {
      worldRef.current?.walkToOffice(slotKey);
      toast(`Heading to ${label}`);
    },
    [toast],
  );

  const sendChat = useCallback(() => {
    const input = chatInputRef.current;
    if (!input) return;
    const text = input.value.trim();
    if (!text) return;
    input.value = "";
    worldRef.current?.say(text);
    multiplayer.sendChat(text);
    const n = nameRef.current;
    const from = n === "You" ? "You" : n;
    pushFeed({
      id: `${Date.now()}-${Math.random()}`,
      kind: "msg",
      from,
      color: colorsRef.current.shirt,
      text,
      zone: zoneRef.current,
      vol: 1,
    });
  }, [pushFeed, multiplayer]);

  const updateColors = useCallback(
    (key: keyof AvatarColors, value: string) => {
      setColors((prev) => {
        const next = normalizeAvatarLook({ ...prev, [key]: value });
        colorsRef.current = next;
        persist({ colors: next });
        if (entered) worldRef.current?.configure({ colors: next });
        return next;
      });
    },
    [persist, entered],
  );

  const replaceColors = useCallback(
    (next: AvatarColors) => {
      const normalized = normalizeAvatarLook(next);
      colorsRef.current = normalized;
      setColors(normalized);
      persist({ colors: normalized });
      if (entered) worldRef.current?.configure({ colors: normalized });
    },
    [persist, entered],
  );

  const enterOffice = useCallback(() => {
    const first = !enteredRef.current;
    const n = nameRef.current;
    const c = colorsRef.current;
    worldRef.current?.configure({ name: n, colors: c });
    enteredRef.current = true;
    setEntered(true);
    setShowOnboarding(false);
    persist({ name: n, colors: c });
    if (first) {
      pushFeed({
        id: `${Date.now()}-${Math.random()}`,
        kind: "sys",
        text: `Connecting to HQ… allow camera & mic. ${SPATIAL_AV_TIP}.`,
      });
      visitedRef.current.commons = true;
      // Daily check-in once per enter session (server dedupes by UTC day).
      if (!checkInSentRef.current) {
        checkInSentRef.current = true;
        checkInMutateRef.current(undefined, {
          onSuccess: (result) => applyAwardResult(result),
        });
      }
    }
  }, [persist, pushFeed, applyAwardResult]);

  const tryAutoEnter = useCallback(() => {
    if (autoEnteredRef.current || enteredRef.current) return;
    if (!worldRef.current) return;
    // Intentional enter: dashboard "Step into HQ" navigates with ?ready=1 after save.
    // Do NOT require lookSavedAt here — it races with profile refetch and left users
    // stuck on "Stepping into HQ…" forever. ready=1 is the enter intent signal.
    const readyParam =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("ready") === "1";
    if (!readyParam) return;
    // Prefer hydrated profile so displayName/colors are applied, but don't block forever.
    autoEnteredRef.current = true;
    enterOffice();
  }, [enterOffice]);

  useEffect(() => {
    tryAutoEnter();
  }, [tryAutoEnter]);

  // Fail-safe: if world is slow or hydrate hangs, still enter after a short wait.
  useEffect(() => {
    const readyParam =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("ready") === "1";
    if (!readyParam || enteredRef.current) return;
    const t = window.setTimeout(() => {
      if (enteredRef.current || autoEnteredRef.current) return;
      if (!worldRef.current) return;
      autoEnteredRef.current = true;
      enterOffice();
    }, 2500);
    return () => window.clearTimeout(t);
  }, [enterOffice]);

  const onWorldReady = useCallback(
    (el: VyllageWorldElement) => {
      bindWorld(el);
      queueMicrotask(() => tryAutoEnter());
    },
    [bindWorld, tryAutoEnter],
  );

  const nextUnlock = useMemo(() => UNLOCKS.find((u) => u.pts > points), [points]);
  const prevUnlockPts = useMemo(() => {
    if (!nextUnlock) return 0;
    const idx = UNLOCKS.findIndex((u) => u.id === nextUnlock.id);
    if (idx <= 0) return 0;
    return UNLOCKS[idx - 1]?.pts ?? 0;
  }, [nextUnlock]);

  const progressPct = useMemo(() => {
    if (!nextUnlock) return "100%";
    const span = nextUnlock.pts - prevUnlockPts;
    const ratio = Math.max(0, Math.min(1, (points - prevUnlockPts) / span));
    return `${Math.round(ratio * 100)}%`;
  }, [nextUnlock, prevUnlockPts, points]);

  const toggleOfficeDoor = useCallback(() => {
    if (!myOffice) return;
    const next = myOffice.doorStatus === "open" ? "closed" : "open";
    updateOffice.mutate({ id: myOffice.id, doorStatus: next });
  }, [myOffice, updateOffice]);

  const saveOfficeCustomization = useCallback(
    (input: {
      displayName: string;
      decorIds: string[];
      doorStatus?: "open" | "closed";
      wallArtFileId?: string | null;
      wallArtPlaceholder?: string | null;
      theme?: { wallColor?: string; rugColor?: string };
    }) => {
      if (!myOffice) return;
      updateOffice.mutate(
        {
          id: myOffice.id,
          displayName: input.displayName,
          decorIds: input.decorIds,
          doorStatus: input.doorStatus,
          wallArtFileId: input.wallArtFileId,
          wallArtPlaceholder: input.wallArtPlaceholder,
          theme: input.theme,
        },
        { onSuccess: () => setShowCustomizeOffice(false) },
      );
    },
    [myOffice, updateOffice],
  );

  const officeByOwnerId = useMemo(() => {
    const map = new Map<string, PersonalOfficeSnapshot>();
    for (const o of officeSnapshots) {
      if (o.ownerUserId) map.set(o.ownerUserId, o);
    }
    return map;
  }, [officeSnapshots]);

  return {
    worldRef,
    chatInputRef,
    feedElRef,
    onWorldReady,
    entered,
    showOnboarding,
    setShowOnboarding,
    name,
    setName,
    colors,
    updateColors,
    replaceColors,
    status,
    statusMeta,
    statusMenuOpen,
    setStatusMenuOpen,
    setStatusManual,
    mic,
    cam,
    toggleMic,
    toggleCam,
    waveNow,
    performAction,
    zone,
    zoneId,
    zonePriv,
    zonePersonal,
    zoneDoorStatus,
    inOwnOffice,
    laptopOpen,
    setLaptopOpen,
    people,
    feed,
    toasts,
    points,
    streak,
    tab,
    setTab,
    meeting,
    meetingViewOpen,
    setMeetingViewOpen,
    standUp,
    minimizeMeetingView,
    openMeetingView,
    sharing: multiplayer.sharing,
    toggleScreenShare,
    localStream: multiplayer.localStream,
    remoteStreams: multiplayer.remoteStreams,
    unlockedIds,
    walkTo,
    walkToOffice,
    officeSnapshots,
    sendChat,
    enterOffice,
    nextUnlock,
    progressPct,
    connection: multiplayer.connection,
    peerCount: multiplayer.peerCount,
    connectionError: multiplayer.error,
    /** Current walkable map: "hq" | "plaza" | … */
    mapId: multiplayer.mapId,
    myOffice,
    toggleOfficeDoor,
    officeDoorPending: updateOffice.isPending,
    showCustomizeOffice,
    setShowCustomizeOffice,
    saveOfficeCustomization,
    officeByOwnerId,
  };
}

/** Full scene state shape — passed to the extracted chrome components. */
export type VyllageSceneState = ReturnType<typeof useVyllageState>;
